import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, Checkbox, Space, Tabs, Typography, Select, Modal } from 'antd';
import { useDispatch } from "react-redux";
import { styled } from 'styled-components';
import Breadcrumb from '../../Common/Breadcrumb';
import HistoryTimeline from '../../Common/HistoryTimeline';
import usecustomStyles from '../../Common/customStyles';
import { Row, Col } from 'antd';
import { APIClient } from "../../helpers/api_helper";
import ProjectSelect from '../../Common/ProjectSelect'; // <-- adjust path if needed
import AccountSelect from '../../Common/AccountSelect'; // <-- adjust path if needed
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const { Title } = Typography;

const customStyles = usecustomStyles();
const TextMuted = styled.div`
  color: ${({ theme }) => theme.token.textMute};
`;
const TextLight = styled.div`
  color: ${({ theme }) => theme.token.textLight};
`;

const api = new APIClient();


// Permissions tab fed by backend list
const AccessAndPermissionsTab = ({
  permList,
  setPermList,
  loading,
  saving,
  setObjectPerms,
  objectPerms
}) => {
  // Per-group selections for Projects & Accounts (UI only, not persisted yet)
  const [groupSelections, setGroupSelections] = useState({});

  // Group permissions by model for nicer layout
  const grouped = useMemo(() => {
    const acc = {};
    permList.forEach((p) => {
      const key = `${p.model}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
    });
    Object.keys(acc).forEach((k) => {
      acc[k].sort((a, b) => a.codename.localeCompare(b.codename));
    });
    return Object.fromEntries(
      Object.entries(acc).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [permList]);

  const togglePerm = (id, next) => {
    setPermList((prev) => prev.map((p) => (p.id === id ? { ...p, has_perm: next } : p)));
  };

  const setGroupProjects = (groupKey, value) => {
    setObjectPerms(prev => ({
      ...prev,
      projects: Array.isArray(value) ? value : [value]
    }));
  };

  const setGroupAccounts = (groupKey, value) => {
    setObjectPerms(prev => ({
      ...prev,
      accounts: Array.isArray(value) ? value : [value]
    }));
  };

  return (
    <Space direction="vertical" size={customStyles.margin} style={{ width: '100%' }}>
      {Object.keys(grouped).length === 0 ? (
        <Card loading={loading}>
          <TextMuted>Ni dovoljenj za prikaz.</TextMuted>
        </Card>
      ) : (
        <Row gutter={[customStyles.margin, customStyles.margin]}>
          {Object.entries(grouped).map(([groupKey, items]) => (
            <Col xs={24} sm={12} key={groupKey}>
              <Card loading={loading}>
                <Title
                  level={5}
                  style={{
                    marginBottom: customStyles.marginXS,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{groupKey}</span>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      const allChecked = items.every((p) => p.has_perm);
                      setPermList((prev) =>
                        prev.map((p) =>
                          p.app_label === items[0].app_label && p.model === items[0].model
                            ? { ...p, has_perm: !allChecked }
                            : p
                        )
                      );
                    }}
                  >
                    {items.every((p) => p.has_perm) ? 'Odznači vse' : 'Izberi vse'}
                  </Button>
                </Title>

                  <Row gutter={customStyles.margin}>
                    <Col xs={24} md={14}>
                      {/* Checkboxes stay the same */}
                      <Checkbox.Group
                        value={items.filter((p) => p.has_perm).map((p) => p.id)}
                        onChange={(checkedIds) => {
                          const checked = new Set(checkedIds);
                          setPermList((prev) =>
                            prev.map((p) =>
                              p.app_label === items[0].app_label && p.model === items[0].model
                                ? { ...p, has_perm: checked.has(p.id) }
                                : p
                            )
                          );
                        }}
                      >
                        <Space direction="vertical">
                          {items.map((p) => (
                            <Checkbox
                              key={p.id}
                              value={p.id}
                              onChange={(e) => togglePerm(p.id, e.target.checked)}
                            >
                              <TextLight style={{ fontWeight: 600 }}>
                                {p.name || p.codename}
                              </TextLight>
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </Col>

                    {groupKey === 'Project' && (
                      <Col xs={24} md={10}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Typography.Text type="secondary" strong>
                            Dodeli projekte
                          </Typography.Text>
                          <ProjectSelect
                            multiple
                            value={objectPerms.projects}
                            onChange={(val) => setGroupProjects(groupKey, val)}
                          />
                        </Space>
                      </Col>
                    )}

                    {groupKey === 'Account' && (
                      <Col xs={24} md={10}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Typography.Text type="secondary" strong>
                            Dodeli račune
                          </Typography.Text>
                          <AccountSelect
                            multiple
                            value={objectPerms.accounts}
                            onChange={(val) => setGroupAccounts(groupKey, val)}
                          />
                        </Space>
                      </Col>
                    )}
                  </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Space>
  );
};

const OverviewTab = ({ username, email, onSubmit, formErrors, saving }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({ username, email });
  }, [username, email, form]);

  return (
    <Card>
      <Title level={5} style={{ marginBottom: customStyles.margin }}>Podatki profila</Title>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          label="Uporabniško ime"
          name="username"
          rules={[{ required: true, message: 'Vnesite uporabniško ime' }]}
          validateStatus={formErrors?.username ? "error" : ""}
          help={formErrors?.username?.join?.(" ") || ""}
        >
          <Input placeholder="Vnesite uporabniško ime" />
        </Form.Item>
        <Form.Item
          label="E-pošta"
          name="email"
          rules={[{ required: true, type: 'email', message: 'Vnesite veljaven e-poštni naslov' }]}
          validateStatus={formErrors?.email ? "error" : ""}
          help={formErrors?.email?.join?.(" ") || ""}
        >
          <Input placeholder="Vnesite e-poštni naslov" />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving} disabled={saving}>Shrani</Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

const PagesUserProfile = () => {
  document.title = 'Profil' + process.env.REACT_APP_PAGE_TITLE;
  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(location.hash ? location.hash.substring(1) : "overview");

  // Local state (replace with selectors wired to backend later)
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [permList, setPermList] = useState([]);
  const [overviewFormErrors, setOverviewFormErrors] = useState({});
  const [objectPerms, setObjectPerms] = useState({ accounts: [], projects: [] });
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);
  const [savingOverview, setSavingOverview] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const userId = useParams()?.id
  const [accessLevel, setAccessLevel] = useState('');

  useEffect(() => {
    if (location.hash) {
      setActiveTab(location.hash.substring(1));
    }
  }, [location.hash]);

  const fetchPermissions = async () => {

    setLoadingPerms(true);
    api.get(`/api/users/${userId}/permissions_setter/`)
      .then(resp => {
        setPermList(Array.isArray(resp?.op_perms) ? resp.op_perms : []);
        setObjectPerms(resp?.object_perms || { accounts: [], projects: [] });
        setAccessLevel(resp?.access_level || 'LIMITED_ORG_ACCESS');
      })
      .finally(() => {
        setLoadingPerms(false);
      });
  };

  const fetchUserProfile = async () => {

    setLoadingPerms(true);
    api.get(`/api/users/${userId}/`)
      .then(resp => {
        setUsername(resp?.username);
        console.log('EMAIL', resp)
        setEmail(resp?.email)
      })
      .finally(() => {
        setLoadingPerms(false);
      });
  };

  const savePermissions = async () => {
    setSavingPerms(true);

    const payload = {
      access_level: accessLevel,
      op_perms: permList.map(({ id, has_perm }) => ({ id, has_perm })),
      object_perms: objectPerms
    };

    api.post(`/api/users/${userId}/permissions_setter/`, payload, {
      successMsg: `Dovoljenja so bila posodobljena!`
    })
    .then(resp => {
      setPermList(Array.isArray(resp?.op_perms) ? resp.op_perms : permList);
      setObjectPerms(resp?.object_perms || objectPerms);
      setAccessLevel(resp?.access_level || accessLevel);
    })
    .finally(() => {
      setSavingPerms(false);
    });
  };

  const saveOverview = async (values) => {
    setSavingOverview(true);
    if (userId){
      api.put(`/api/users/${userId}/`, values, {
        successMsg: `Profil je bil posodobljen!`
      })
      .then(resp => {
        setUsername(resp?.username || values.username);
        setEmail(resp?.email || values.email);
      })
      .finally(() => setSavingOverview(false));
    } else {
      api.post(`/api/users/`, values, {
        successMsg: `Uporabnik je bil ustvarjen – povabilo je bilo poslano!`
      })
      .then(() => {
        navigate('/org_settings#users');
      })
      .catch(err => {
        setOverviewFormErrors(err.response.data);
        setSavingOverview(false);
      });
    }
  };

  const handleOverviewSave = (values) => {
    setOverviewFormErrors({})
    setUsername(values.username);
    setEmail(values.email);
    saveOverview(values);
  };

  const handleDeleteUser = () => {
    api.delete(`/api/users/${userId}/`, {}, { successMsg: `Uporabnik je bil izbrisan!` })
      .then(() => {
        setShowDeleteConfirmModal(false);
        navigate('/org_settings#users');
      })
      .catch(error => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (userId) {
      fetchPermissions();
      fetchUserProfile();
    }
  }, [userId]);

  const items = [
    {
      key: 'overview',
      label: <span>Pregled</span>,
      children: <OverviewTab username={username} email={email} onSubmit={handleOverviewSave} formErrors={overviewFormErrors} saving={savingOverview} />,
    },
    {
      key: 'access_permissions',
      label: <span>Dostop in dovoljenja</span>,
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size={customStyles.margin}>
          <Card>
            <Title level={5} style={{ marginBottom: customStyles.margin }}>Raven dostopa</Title>
            <Select value={accessLevel} onChange={setAccessLevel} style={{ width: 240 }}>
              <Select.Option value="FULL_ORG_ACCESS">Poln dostop do organizacije</Select.Option>
              <Select.Option value="LIMITED_ORG_ACCESS">Omejen dostop do organizacije</Select.Option>
            </Select>
          </Card>
          {accessLevel === 'LIMITED_ORG_ACCESS' ? (
            <AccessAndPermissionsTab
              permList={permList}
              setPermList={setPermList}
              objectPerms={objectPerms}
              setObjectPerms={setObjectPerms}
              loading={loadingPerms}
              saving={savingPerms}
            />
          ) : (
            <Card>
              <Typography.Text type="secondary">
                Podeljen je poln dostop do organizacije. Dodatna konfiguracija ni potrebna.
              </Typography.Text>
            </Card>
          )}
          <Button
            type="primary"
            onClick={savePermissions}
            loading={savingPerms}
            disabled={loadingPerms}
          >
            Shrani
          </Button>
        </Space>
      ),
    },
    ...(userId ? [{
      key: 'history',
      label: <span>Zgodovina</span>,
      children: <HistoryTimeline model="User" objectId={userId} />,
    }] : []),
  ];

  return (
    <>
      <Breadcrumb parentTitle="Strani" pageTitle="Profil" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>{username}</h1>
        <Button danger onClick={() => setShowDeleteConfirmModal(true)}><DeleteOutlined /></Button>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          navigate(`#${key}`, { replace: true });
        }}
        items={items}
      />
      <Modal
        open={showDeleteConfirmModal}
        title="Potrdi brisanje"
        onOk={handleDeleteUser}
        onCancel={() => setShowDeleteConfirmModal(false)}
        okText="Izbriši"
        cancelText="Prekliči"
        okButtonProps={{ danger: true }}
      >
        <p>Ali res želite izbrisati tega uporabnika?</p>
      </Modal>
    </>
  );
};

export default PagesUserProfile;
